import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../data/base";

/**
 * Trova l'ID della subscription che contiene un catalogo specifico
 * @param {string} catalogueId - ID del catalogo da cercare
 * @returns {Promise<string|null>} - ID della subscription o null se non trovata
 */
export const getSubscriptionIdByCatalogueId = async (catalogueId) => {
    try {
        if (!catalogueId) {
            return null;
        }

        // Query per trovare tutte le subscriptions attive
        const subscriptionsQuery = query(
            collection(db, "ARShadesSubscriptions"),
            where("status", "in", ["Active", "Renewed"])
        );

        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

        // Cerca la subscription che contiene il catalogueId in list_catalogues_refs
        for (const subscriptionDoc of subscriptionsSnapshot.docs) {
            const subscriptionData = subscriptionDoc.data();
            const subscriptionId = subscriptionDoc.id;
            const catalogueRefs = subscriptionData.list_catalogues?.list_catalogues_refs || [];

            // Verifica se il catalogo è presente in list_catalogues_refs
            if (catalogueRefs.includes(catalogueId)) {
                return subscriptionId; // Restituisce l'ID del documento (doc ref)
            }
        }

        return null;

    } catch (error) {
        console.error("Error retrieving subscription ID for catalogue:", catalogueId, error);
        return null;
    }
};

/**
 * Recupera il nome della subscription per un catalogo specifico
 * @param {string} catalogueId - ID del catalogo
 * @returns {Promise<string>} - Nome della subscription o fallback
 */
export const getSubscriptionNameByCatalogueId = async (catalogueId) => {
    try {
        if (!catalogueId) {
            return '-';
        }

        // Query per trovare tutte le subscriptions
        const subscriptionsQuery = query(
            collection(db, "ARShadesSubscriptions"),
            where("status", "in", ["Active", "Renewed"])
        );

        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

        // Cerca la subscription che contiene il catalogueId in list_catalogues_refs
        for (const subscriptionDoc of subscriptionsSnapshot.docs) {
            const subscriptionData = subscriptionDoc.data();

            // Verifica se il catalogo è presente in list_catalogues_refs
            if (subscriptionData.list_catalogues?.list_catalogues_refs?.includes(catalogueId)) {
                return subscriptionData.name || subscriptionData.id || '-';
            }
        }

        // Se non trovato, ritorna un fallback
        return '-';

    } catch (error) {
        console.error("Error retrieving subscription name for catalogue:", catalogueId, error);
        return '-';
    }
};

/**
 * Recupera i nomi delle subscriptions per più cataloghi
 * @param {Array<string>} catalogueIds - Array di ID dei cataloghi
 * @returns {Promise<Object>} - Oggetto con catalogueId come chiave e nome subscription come valore
 */
export const getSubscriptionNamesByCatalogueIds = async (catalogueIds) => {
    try {
        if (!catalogueIds || catalogueIds.length === 0) {
            return {};
        }

        // Query per trovare tutte le subscriptions attive
        const subscriptionsQuery = query(
            collection(db, "ARShadesSubscriptions"),
            where("status", "in", ["Active", "Renewed"])
        );

        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
        const subscriptionMap = {};

        // Inizializza tutti i catalogueIds con fallback
        catalogueIds.forEach(id => {
            subscriptionMap[id] = '-';
        });

        // Cerca le subscriptions che contengono i catalogueIds
        subscriptionsSnapshot.docs.forEach(subscriptionDoc => {
            const subscriptionData = subscriptionDoc.data();
            const subscriptionName = subscriptionData.name || subscriptionData.id || '-';

            // Verifica se qualcuno dei cataloghi è presente in list_catalogues_refs
            if (subscriptionData.list_catalogues?.list_catalogues_refs) {
                subscriptionData.list_catalogues.list_catalogues_refs.forEach(catalogueId => {
                    if (catalogueIds.includes(catalogueId)) {
                        subscriptionMap[catalogueId] = subscriptionName;
                    }
                });
            }
        });

        return subscriptionMap;

    } catch (error) {
        console.error("Error retrieving subscription names for catalogues:", error);
        return catalogueIds.reduce((acc, id) => {
            acc[id] = '-';
            return acc;
        }, {});
    }
};

/**
 * Recupera i nomi delle subscriptions per più cataloghi (VERSIONE PER MyCatalogues - SENZA FILTRO PER DATA)
 * Questa versione include TUTTE le subscription indipendentemente dal loro status
 * @param {Array<string>} catalogueIds - Array di ID dei cataloghi
 * @returns {Promise<Object>} - Oggetto con catalogueId come chiave e nome subscription come valore
 */
export const getSubscriptionNamesByCatalogueIdsForMyCatalogues = async (catalogueIds) => {
    try {
        if (!catalogueIds || catalogueIds.length === 0) {
            return {};
        }

        // Query per trovare TUTTE le subscriptions senza filtro per status
        const subscriptionsQuery = collection(db, "ARShadesSubscriptions");

        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
        const subscriptionMap = {};

        // Inizializza tutti i catalogueIds con fallback
        catalogueIds.forEach(id => {
            subscriptionMap[id] = '-';
        });

        // Cerca le subscriptions che contengono i catalogueIds
        subscriptionsSnapshot.docs.forEach(subscriptionDoc => {
            const subscriptionData = subscriptionDoc.data();
            const subscriptionName = subscriptionData.name || subscriptionData.id || '-';

            // Verifica se qualcuno dei cataloghi è presente in list_catalogues_refs
            if (subscriptionData.list_catalogues?.list_catalogues_refs) {
                subscriptionData.list_catalogues.list_catalogues_refs.forEach(catalogueId => {
                    if (catalogueIds.includes(catalogueId)) {
                        subscriptionMap[catalogueId] = subscriptionName;
                    }
                });
            }
        });

        return subscriptionMap;

    } catch (error) {
        console.error("Error retrieving subscription names for catalogues (MyCatalogues version):", error);
        return catalogueIds.reduce((acc, id) => {
            acc[id] = '-';
            return acc;
        }, {});
    }
}; 